import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  InviteResponse,
  PasswordResetLink,
  StaffInvitationInfo,
  StaffInviteRequest,
  StaffMember,
  TeamOverview,
  TotpSetup,
  UpdateStaffRequest,
} from '@ste/shared';
import { postJson, rootFetch, useAuth } from './auth.ts';
import { API_ROOT, useConnection } from './connection.ts';

// STE Connect team management for admins (ADR-0013)

const json = (body: unknown, method = 'POST') => ({ method, body: JSON.stringify(body) });

export function useIsTeamAdmin(): boolean {
  const mode = useConnection((s) => s.mode);
  const globalRole = useAuth((s) => s.user?.globalRole);
  return mode === 'cloud' && globalRole === 'admin';
}

export const useTeam = (enabled: boolean) =>
  useQuery({
    queryKey: ['cloud', 'team'],
    queryFn: () => rootFetch<TeamOverview>('/team'),
    enabled,
  });

export function useTeamCommands() {
  const client = useQueryClient();
  const refresh = () => void client.invalidateQueries({ queryKey: ['cloud', 'team'] });
  return {
    invite: useMutation({
      mutationFn: (request: StaffInviteRequest) =>
        rootFetch<InviteResponse>('/team/invitations', json(request)),
      onSuccess: refresh,
    }),
    revoke: useMutation({
      mutationFn: (id: string) => rootFetch<void>(`/team/invitations/${id}`, { method: 'DELETE' }),
      onSuccess: refresh,
    }),
    remove: useMutation({
      mutationFn: (userId: string) => rootFetch<void>(`/team/${userId}`, { method: 'DELETE' }),
      onSuccess: refresh,
    }),
    update: useMutation({
      mutationFn: ({ userId, ...change }: UpdateStaffRequest & { userId: string }) =>
        rootFetch<StaffMember>(`/team/${userId}`, json(change, 'PATCH')),
      onSuccess: refresh,
    }),
  };
}

export async function fetchStaffInvitation(token: string): Promise<StaffInvitationInfo> {
  const res = await fetch(`${API_ROOT}/team-invitations/${encodeURIComponent(token)}`);
  if (!res.ok)
    throw new Error(
      ((await res.json().catch(() => ({}))) as { message?: string }).message ?? 'Einladung ungültig',
    );
  return (await res.json()) as StaffInvitationInfo;
}

export const startStaffTotp = (token: string) =>
  postJson<TotpSetup>(`/team-invitations/${encodeURIComponent(token)}/totp`, {});

export const createStaffResetLink = (userId: string, resetTotp: boolean) =>
  rootFetch<PasswordResetLink>(`/team/${userId}/password-reset`, json({ resetTotp }));

export const createMemberResetLink = (siteId: string, userId: string, resetTotp: boolean) =>
  rootFetch<PasswordResetLink>(`/sites/${siteId}/members/${userId}/password-reset`, json({ resetTotp }));
